var Component        = require("enyo/Component"),
    FacebookPixel    = require("app/Analytics/FacebookPixel"),
    GoogleAnalytics  = require("app/Analytics/GoogleAnalytics"),
    GoogleTagManager = require("app/Analytics/GoogleTagManager"),
    Hotjar           = require("app/Analytics/Hotjar"),
    TikTokPixel      = require("app/Analytics/TikTokPixel"),
    TraceDock        = require("app/Analytics/TraceDock"),
    kind             = require("enyo/kind"),
    logger           = require("enyo/logger");

module.exports = kind(/** @lends  app.Analytics.AnalyticsService.prototype */ {

    /**
     * @private
     */
    name: "app.Analytics.AnalyticsService",

    /**
     * @private
     */
    kind: Component,

    /**
     * @private
     */
    components: [{
        name: "facebookPixel",
        kind: FacebookPixel
    }, {
        name: "googleAnalytics",
        kind: GoogleAnalytics
    }, {
        name: "googleTagManager",
        kind: GoogleTagManager
    }, {
        name: "hotjar",
        kind: Hotjar
    }, {
        name: "tikTokPixel",
        kind: TikTokPixel
    }, {
        name: "traceDock",
        kind: TraceDock
    }],

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.observe("app.$.authService.user", this.onUserChange);
            this.onUserChange();
        };
    }),

    /**
     * Mark services as loaded when their loader is ready.
     *
     * @param  {string}         loader    The loader
     * @param  {Array<string>}  services  The services to mark as loaded
     * @public
     */
    onLoaderReady: function(loader, services) {
        services = Array.isArray(services) ? services : [];

        services.forEach(this.bindSafely(function(name) {
            var $service = this.$[name] || null;

            // Mark as loaded if needed
            if (
                $service
                && !$service.get("isReady")
                && $service.getLoader() === loader
            ) {
                $service.onLoad();
            }
        }));
    },

    /**
     * Verify if a particular service is loaded.
     *
     * @param    {string}   service  The name of the service
     * @returns  {boolean}  Whether a service by the provided name has been
     *                      loaded
     * @public
     */
    isServiceLoaded: function(service) {
        if (this.$[service]) {
            return this.$[service].get("isReady");
        }
        else {
            return false;
        }
    },

    /**
     * Get a promise that resolves when the requested service is loaded.
     *
     * @param    {string}   service  The name of the service
     * @param    {number}   timeout  Max. waiting time in seconds
     * @returns  {Promise}  A promise that resolves when a service by the
     *                      provided name has been loaded
     * @public
     */
    getServiceLoadedPromise: function(service, timeout) {
        var servicePromise,
            timeoutPromise,
            timeoutResolver,
            timeoutTimer,
            errorMsg;

        if (this.$[service]) {
            servicePromise = this.$[service].get("isReadyPromise");
        }

        if (servicePromise instanceof Promise) {
            if (timeout) {
                timeoutPromise = new Promise(this.bindSafely(function(resolve) {
                    timeoutResolver = resolve;
                }));

                timeoutTimer = setTimeout(function() {
                    timeoutResolver(false);
                }, timeout);

                servicePromise.then(this.bindSafely(function() {
                    clearTimeout(timeoutTimer);
                    timeoutResolver(true);
                }));

                return timeoutPromise;
            }
            else {
                return servicePromise;
            }
        }
        else {
            errorMsg = "Cannot get isReadyPromise for Analytics Service: `" + String(service) + "`.";
            logger.error(errorMsg);

            if (typeof Sentry !== "undefined") {
                Sentry.captureException(new Error(errorMsg));
            }

            return Promise.resolve(false);
        }
    },

    /**
     * Apply the identity of the new user.
     *
     * @protected
     */
    onUserChange: function() {
        this.$.googleAnalytics.updateUserIdentity();
    },

    /**
     * Send a PageView event if the 3rd party script supports this.
     *
     * @param  {Object=}  parameters  Optional set of extra parameters to send
     *                                along.
     * @public
     */
    sendPageView: function(parameters) {
        this.$.facebookPixel.sendPageView(parameters);
        this.$.googleAnalytics.sendPageView(parameters);
    },

    /**
     * Send an event if the 3rd party script supports this.
     *
     * @param  {string=}  category  The event category.
     * @param  {string}   action    The event action.
     * @param  {string=}  label     The event label.
     * @param  {string=}  value     The event value.
     * @param  {Object=}  params    Optional params.
     * @abstract
     * @public
     */
    sendEvent: function(category, action, label, value, params) {
        this.$.facebookPixel.sendEvent(category, action, label, value, params);
        this.$.googleAnalytics.sendEvent(category, action, label, value, params);
    }

});
